import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { Link as RouterLink } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getRevertCancelCheck, revertCancelTransaction } from "../../../services";
import {
  RevertCancelCheck,
  RevertCancelIssue,
  RevertCancelLink,
  RevertCancelNegativeStockItem,
  RevertCancelResult,
} from "../../../types/revert-cancel.type";
import { formatNumber } from "../../../utils";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const REASON_MAX_LENGTH = 500;

/** Where a step's link points. Bills route by type; anything but invoice is a cash sale. */
export const revertCancelLinkPath = (link: RevertCancelLink): string => {
  if (link.kind === "sale_report") return `/report/sale/details/${link.uuid}`;
  return link.transaction_type === "invoice"
    ? `/sales/invoice/details/${link.uuid}`
    : `/sales/cash-sales/details/${link.uuid}`;
};

const revertCancelLinkLabel = (link: RevertCancelLink): string =>
  link.kind === "sale_report" ? "เปิดรายงานการขาย" : "เปิดบิล";

type View =
  | { kind: "checking" }
  | { kind: "check_failed" }
  | { kind: "checked"; check: RevertCancelCheck }
  | { kind: "recalc_failed"; result: RevertCancelResult };

interface RevertCancelDialogProps {
  open: boolean;
  uuid: string;
  onClose: () => void;
  /** Called once the bill is restored; the caller refetches the bill. */
  onReverted: () => void;
}

const linkSx = { color: "link.main", fontWeight: 600 } as const;

function StepLink({
  link,
  onNavigate,
}: {
  link: RevertCancelLink;
  onNavigate: () => void;
}) {
  return (
    <Link
      component={RouterLink}
      to={revertCancelLinkPath(link)}
      onClick={onNavigate}
      sx={linkSx}
    >
      {revertCancelLinkLabel(link)}
    </Link>
  );
}

function IssueSteps({
  issue,
  onNavigate,
}: {
  issue: RevertCancelIssue;
  onNavigate: () => void;
}) {
  if (!issue.steps?.length) return null;
  const steps = [...issue.steps].sort((a, b) => a.order - b.order);
  return (
    <Stack component="ol" gap={0.5} sx={{ pl: 3, m: 0, mt: 1 }}>
      {steps.map((step) => (
        <Typography key={step.order} component="li" variant="body2">
          {step.text}
          {step.link && (
            <>
              {" "}
              <StepLink link={step.link} onNavigate={onNavigate} />
            </>
          )}
        </Typography>
      ))}
    </Stack>
  );
}

function readNegativeStockItems(
  issue: RevertCancelIssue,
): RevertCancelNegativeStockItem[] {
  const items = issue.details?.items;
  return Array.isArray(items) ? (items as RevertCancelNegativeStockItem[]) : [];
}

function WarningDetails({ issue }: { issue: RevertCancelIssue }) {
  if (issue.code === "NEGATIVE_STOCK") {
    const items = readNegativeStockItems(issue);
    if (!items.length) return null;
    return (
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>สินค้า</TableCell>
            <TableCell align="right">คงเหลือตอนนี้</TableCell>
            <TableCell align="right">หลังกู้คืน</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.product_code}>
              <TableCell>
                {item.product_code}
                {item.product_name ? ` ${item.product_name}` : ""}
              </TableCell>
              <TableCell align="right">
                {formatNumber(item.current_qty, { decimals: 0 })}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color:
                    Number(item.after_qty) < 0 ? "error.main" : undefined,
                  fontWeight: 600,
                }}
              >
                {formatNumber(item.after_qty, { decimals: 0 })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  if (issue.code === "ETAX_DOCUMENT_EXISTS") {
    const status = issue.details?.status;
    const documentStatus = issue.details?.document_status;
    if (status == null && documentStatus == null) return null;
    return (
      <Typography variant="body2" sx={{ mt: 1 }}>
        สถานะ eTax: {status != null ? String(status) : "-"} · สถานะเอกสาร:{" "}
        {documentStatus != null ? String(documentStatus) : "-"}
      </Typography>
    );
  }
  return null;
}

function WarningAlerts({
  warnings,
  onNavigate,
}: {
  warnings: RevertCancelIssue[];
  onNavigate: () => void;
}) {
  if (!warnings.length) return null;
  return (
    <Stack gap={1.5}>
      {warnings.map((warning) => (
        <Alert key={warning.code} severity="warning">
          <AlertTitle>{warning.title}</AlertTitle>
          {warning.reason}
          <WarningDetails issue={warning} />
          <IssueSteps issue={warning} onNavigate={onNavigate} />
        </Alert>
      ))}
    </Stack>
  );
}

export default function RevertCancelDialog({
  open,
  uuid,
  onClose,
  onReverted,
}: RevertCancelDialogProps) {
  const [view, setView] = useState<View>({ kind: "checking" });
  const [reason, setReason] = useState<string>("");
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const runCheck = useCallback(async () => {
    setView({ kind: "checking" });
    setAcknowledged(false);
    try {
      const { data } = await getRevertCancelCheck(uuid);
      if (data?.data) setView({ kind: "checked", check: data.data });
      else setView({ kind: "check_failed" });
    } catch {
      // The interceptor has already shown the error.
      setView({ kind: "check_failed" });
    }
  }, [uuid]);

  // Every open starts from a fresh check and an empty draft.
  useEffect(() => {
    if (!open) return;
    setReason("");
    setAcknowledged(false);
    runCheck();
  }, [open, runCheck]);

  const handleClose = () => {
    if (submitting) return;
    if (view.kind === "recalc_failed") {
      onClose();
      onReverted();
      return;
    }
    onClose();
  };

  const check = view.kind === "checked" ? view.check : null;
  const hasWarnings = Boolean(check?.warnings?.length);
  const trimmedReason = reason.trim();
  const canSubmit =
    Boolean(check?.can_revert) &&
    trimmedReason.length > 0 &&
    (!hasWarnings || acknowledged);

  const handleSubmit = async () => {
    if (!check || !canSubmit) return;
    try {
      setSubmitting(true);
      const { data } = await revertCancelTransaction(uuid, {
        reason: trimmedReason,
        acknowledge_warnings: hasWarnings ? true : undefined,
      });
      const result = data?.data;
      if (result?.sale_report && !result.sale_report.recalculated) {
        // Keep the dialog open to tell master the report still needs a manual update;
        // the refetch waits until it is dismissed, because the refetched bill is no
        // longer cancelled and would unmount this dialog.
        setView({ kind: "recalc_failed", result });
        return;
      }
      onClose();
      onReverted();
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 409) {
        // The situation changed since the check; show what blocks it now.
        await runCheck();
      }
      // Any other error: the interceptor shows it and the dialog stays as it is.
    } finally {
      setSubmitting(false);
    }
  };

  // A step link leaves this bill, so the dialog must not linger over the next page.
  const handleNavigate = () => onClose();

  const renderTitle = () => {
    if (view.kind === "checking") return "กู้คืนบิล";
    if (view.kind === "check_failed") return "กู้คืนบิล";
    if (view.kind === "recalc_failed") return "กู้คืนบิลแล้ว";
    if (!view.check.can_revert) return "กู้คืนบิลนี้ไม่ได้";
    return `ยืนยันกู้คืนบิล ${view.check.transaction_no}`;
  };

  const renderBody = () => {
    if (view.kind === "checking") {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
          aria-label="กำลังตรวจสอบ"
        >
          <CircularProgress />
        </Box>
      );
    }
    if (view.kind === "check_failed") {
      return (
        <DialogContentText>
          ตรวจสอบบิลนี้ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
        </DialogContentText>
      );
    }
    if (view.kind === "recalc_failed") {
      const saleReport = view.result.sale_report;
      return (
        <Alert severity="warning">
          {
            "กู้คืนบิลแล้ว แต่คำนวณรายงานการขายใหม่ไม่สำเร็จ กรุณากด 'อัพเดทข้อมูล' ที่รายงานการขาย"
          }
          {saleReport?.error && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              สาเหตุ: {saleReport.error}
            </Typography>
          )}
          {saleReport && (
            <Box sx={{ mt: 1 }}>
              <Link
                component={RouterLink}
                to={revertCancelLinkPath({
                  kind: "sale_report",
                  uuid: saleReport.uuid,
                })}
                onClick={() => {
                  onClose();
                  onReverted();
                }}
                sx={linkSx}
              >
                เปิดรายงานการขาย
              </Link>
            </Box>
          )}
        </Alert>
      );
    }

    const { check: current } = view;
    if (!current.can_revert) {
      return (
        <Stack gap={2}>
          <DialogContentText>
            ต้องแก้ตามขั้นตอนด้านล่างก่อน จึงจะกู้คืนบิลได้
          </DialogContentText>
          <Stack component="ul" gap={1.5} sx={{ pl: 3, m: 0 }}>
            {current.blockers.map((blocker) => (
              <Box component="li" key={blocker.code}>
                <Typography variant="body2" fontWeight={700}>
                  {blocker.title}
                </Typography>
                <Typography variant="body2">{blocker.reason}</Typography>
                <IssueSteps issue={blocker} onNavigate={handleNavigate} />
              </Box>
            ))}
          </Stack>
          <WarningAlerts
            warnings={current.warnings ?? []}
            onNavigate={handleNavigate}
          />
        </Stack>
      );
    }

    return (
      <Stack gap={2}>
        <DialogContentText component="div">
          บิลจะกลับไปเป็นสถานะก่อนถูกยกเลิก
          <Stack component="ul" gap={0.5} sx={{ pl: 3, m: 0, mt: 1 }}>
            <Typography component="li" variant="body2">
              สต็อกบนรถจะถูกตัดกลับอีกครั้ง
            </Typography>
            <Typography component="li" variant="body2">
              รายงานการขายของบิลนี้จะถูกคำนวณใหม่อัตโนมัติ
            </Typography>
          </Stack>
        </DialogContentText>
        <WarningAlerts
          warnings={current.warnings ?? []}
          onNavigate={handleNavigate}
        />
        {hasWarnings && (
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                disabled={submitting}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label="รับทราบคำเตือนและยืนยันจะกู้คืน"
          />
        )}
        <Box>
          <TextField
            fullWidth
            multiline
            required
            rows={3}
            label="เหตุผลที่กู้คืน"
            value={reason}
            disabled={submitting}
            onChange={(e) =>
              setReason(e.target.value.slice(0, REASON_MAX_LENGTH))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "right", mt: 0.5 }}
          >
            {reason.length}/{REASON_MAX_LENGTH}
          </Typography>
        </Box>
      </Stack>
    );
  };

  const renderActions = () => {
    if (view.kind === "checked" && view.check.can_revert) {
      return (
        <>
          <Button disabled={submitting} onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            ยืนยันกู้คืน
          </Button>
        </>
      );
    }
    return (
      <Button onClick={handleClose}>
        ปิด
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { [compact]: { maxHeight: "94vh" } } },
      }}
    >
      <DialogTitle>{renderTitle()}</DialogTitle>
      <DialogContent dividers sx={{ [compact]: { overflowY: "auto" } }}>
        {renderBody()}
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
}
